import { useState } from 'react';

import { Hint } from '@/components/prototype/hint';
import { identicon, IDENTICON_VIEWBOX } from '@/lib/identicon';
import { cn } from '@/lib/utils';

/*
 * Player avatar. Players are in-game names and carry no picture of their own,
 * so the default is a name-derived pixel identicon (see lib/identicon). Only a
 * registered user who uploaded one has `src` - that renders as a face image and
 * falls back to the identicon if the file fails to load.
 */

function Identicon({ name }: { name: string }) {
  const { color, path } = identicon(name);

  return (
    <svg
      viewBox={IDENTICON_VIEWBOX}
      className="h-full w-full"
      shapeRendering="crispEdges"
      aria-hidden
      focusable="false"
      data-testid="avatar-identicon"
    >
      <path d={path} fill={color} />
    </svg>
  );
}

interface PlayerAvatarProps {
  name: string;
  size?: number;
  /** Avatar URL (own upload or Discord CDN); null/undefined → identicon. */
  src?: string | null;
  className?: string;
  /** Hide from assistive tech when the name is already rendered next to it. */
  decorative?: boolean;
  /** Tooltip text in place of the bare name; the accessible name stays `name`. */
  hint?: string;
}

export function PlayerAvatar({
  name,
  size = 28,
  src,
  className,
  decorative = false,
  hint,
}: PlayerAvatarProps) {
  // A stale/deleted upload must not leave a broken image - drop to the
  // identicon for the rest of this mount.
  const [isBroken, setBroken] = useState(false);
  const showImage = Boolean(src) && !isBroken;

  return (
    <Hint label={decorative ? null : (hint ?? name)}>
      <span
        className={cn(
          'bg-panel-in inline-flex shrink-0 items-center justify-center overflow-hidden border border-black',
          className,
        )}
        style={{ width: size, height: size }}
        role={decorative ? undefined : 'img'}
        aria-label={decorative ? undefined : name}
        aria-hidden={decorative || undefined}
      >
        {showImage ? (
          <img
            src={src ?? undefined}
            alt=""
            className="h-full w-full object-cover"
            style={{ imageRendering: size < 48 ? 'auto' : 'pixelated' }}
            // Sources may be third-party (Discord CDN): never leak the page URL.
            referrerPolicy="no-referrer"
            loading="lazy"
            onError={() => setBroken(true)}
          />
        ) : (
          <Identicon name={name} />
        )}
      </span>
    </Hint>
  );
}
